import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Alert,
  Modal,
  FlatList,
  Dimensions,
  Platform,
} from 'react-native';
import { Audio } from 'expo-av';
import { CameraView, useCameraPermissions } from 'expo-camera';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { BarcodeDetector } from "barcode-detector/ponyfill";
import { MacroService, FoodService, PlanService } from '../../services/ApiService';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

const MacroTrackerScreen = () => {
  const insets = useSafeAreaInsets();
  const [todayLog, setTodayLog] = useState(null);
  const [macroTargets, setMacroTargets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFoodSearch, setShowFoodSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showAddMeal, setShowAddMeal] = useState(false);
  const [selectedFood, setSelectedFood] = useState(null);
  const [mealQuantity, setMealQuantity] = useState('1');
  const [mealUnit, setMealUnit] = useState('serving');
  const [calculatedMacros, setCalculatedMacros] = useState(null);
  const [manualMacros, setManualMacros] = useState({
    calories: '',
    protein: '',
    carbs: '',
    fats: '',
  });
  const [useManualMacros, setUseManualMacros] = useState(false);
  const [editingMealIndex, setEditingMealIndex] = useState(null);

  // Speech Recognition State
  const [recording, setRecording] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const recordingRef = useRef(null);

  const [showNlpInput, setShowNlpInput] = useState(false);
  const [nlpQuery, setNlpQuery] = useState('');
  const [showQuickAddSummary, setShowQuickAddSummary] = useState(false);
  const [quickAddItems, setQuickAddItems] = useState([]);
  const nlpInputRef = useRef(null);

  // Unit options based on food type
  const getUnitOptions = () => {
    if (!selectedFood) {
      return ['serving', 'cups', 'g'];
    }

    const options = ['serving', 'cups', 'g'];
    const foodUnit = selectedFood.servingSizeUnit?.toLowerCase() || 'g';

    // If food has a specific unit, prioritize it
    if (foodUnit === 'cup' || foodUnit === 'cups') {
      return ['cups', 'serving', 'g'];
    } else if (foodUnit === 'oz' || foodUnit === 'ounce') {
      return ['serving', 'oz', 'g'];
    } else {
      return ['serving', 'cups', 'g'];
    }
  };

  // Calculate macros when quantity or unit changes (real-time updates)
  useEffect(() => {
    const calculateMacrosOnChange = async () => {
      if (selectedFood && !useManualMacros && mealQuantity && !isNaN(parseFloat(mealQuantity)) && parseFloat(mealQuantity) > 0) {
        try {
          // Calculate macros in real-time based on selected unit
          const macros = await FoodService.calculateMacros(
            selectedFood,
            parseFloat(mealQuantity),
            mealUnit
          );
          setCalculatedMacros(macros);
        } catch (error) {
          console.error('Macro calculation error:', error);
          setCalculatedMacros(null);
        }
      } else {
        setCalculatedMacros(null);
      }
    };

    // Debounce the calculation slightly to avoid too many API calls
    const timeoutId = setTimeout(() => {
      calculateMacrosOnChange();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [selectedFood, mealQuantity, mealUnit, useManualMacros]);

  // Auto-expand and scroll text input when speech recognition ends
  useEffect(() => {
    if (nlpQuery && nlpInputRef.current && !isRecording) {
      // Small delay to ensure the text is rendered
      setTimeout(() => {
        if (nlpInputRef.current) {
          nlpInputRef.current.focus();
          // Scroll to end of text
          nlpInputRef.current.setNativeProps({
            selection: { start: nlpQuery.length, end: nlpQuery.length }
          });
        }
      }, 150);
    }
  }, [nlpQuery, isRecording]);

  useEffect(() => {
    loadMacroData();
  }, []);

  const loadMacroData = async () => {
    try {
      const [todayData, plan] = await Promise.all([
        MacroService.getTodayMacros(),
        PlanService.getCurrentPlan(),
      ]);

      setTodayLog(todayData.todayLog);
      setMacroTargets(todayData.macroTargets || plan?.macroTargets);
    } catch (error) {
      console.error('Macro data error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Debounce Food Search
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      // Only search if query is at least 3 chars long to reduce API calls
      if (searchQuery.trim().length >= 3) {
        searchFoods();
      } else if (searchQuery.trim().length === 0) {
        setSearchResults([]);
      }
    }, 600); // 600ms delay for optimization

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  const searchFoods = async () => {
    // If called directly (e.g. enter key), we can proceed, but the effect covers it mostly
    if (!searchQuery.trim()) return;

    setSearching(true);
    try {
      const results = await FoodService.searchFoods(searchQuery);
      setSearchResults(results);
    } catch (error) {
      console.error('Food search error:', error);
      // Fail silently for type-ahead to not annoy user, or show small indicator
    } finally {
      setSearching(false);
    }
  };

  const handleNlpSearch = async () => {
    if (!nlpQuery.trim()) return;

    setSearching(true);
    try {
      const results = await FoodService.analyzeFoodText(nlpQuery);
      if (results && results.length > 0) {
        setQuickAddItems(results);
        setShowQuickAddSummary(true);
        // setNlpQuery(''); // Keep text for reference
      } else {
        Alert.alert('No results', 'Could not identify foods from text.');
      }
    } catch (error) {
      console.error('NLP search error:', error);
      Alert.alert('Error', 'Failed to analyze text');
    } finally {
      setSearching(false);
      setShowNlpInput(false);
    }
  };

  const startRecording = async () => {
    try {
      setIsRecording(true);
      setNlpQuery(''); // Clear previous text to show 'Listening...' cleanly

      const permission = await Audio.requestPermissionsAsync();
      if (!permission.granted) {
        setIsRecording(false);
        return;
      }

      await Audio.setAudioModeAsync({
        allowsRecordingIOS: true,
        playsInSilentModeIOS: true
      });

      const { recording } = await Audio.Recording.createAsync(
        Audio.RecordingOptionsPresets.HIGH_QUALITY
      );

      recordingRef.current = recording;

    } catch (err) {
      console.error('Start recording error:', err);
      setIsRecording(false);
    }
  };


  const stopRecording = async () => {
    try {
      if (!recordingRef.current) {
        console.warn('Stop called but no active recording');
        setIsRecording(false);
        return;
      }

      await recordingRef.current.stopAndUnloadAsync();
      const uri = recordingRef.current.getURI();
      recordingRef.current = null;
      setIsRecording(false);

      // Upload audio
      const formData = new FormData();

      if (Platform.OS === 'web') {
        const audioBlob = await fetch(uri).then(r => r.blob());
        formData.append('audio', audioBlob, 'meal.m4a');
      } else {
        formData.append('audio', {
          uri,
          name: 'meal.m4a',
          type: 'audio/m4a'
        });
      }

      const response = await fetch(`https://training-application-q6ln.onrender.com/api/food/analyze-audio`, {
        method: 'POST',
        body: formData,
        // headers: { 'Content-Type': 'multipart/form-data' } // Removed to let browser generate boundary
      });

      const data = await response.json();

      // Prevent crash if transcribedText is missing
      const text = data.transcribedText || '';
      setNlpQuery(text);

      // Scroll to end of text input after a brief delay to ensure content is visible
      if (text && nlpInputRef.current) {
        setTimeout(() => {
          nlpInputRef.current?.focus();
          // For multiline TextInput, we need to scroll to the end
          if (nlpInputRef.current) {
            nlpInputRef.current.setNativeProps({
              selection: { start: text.length, end: text.length }
            });
          }
        }, 100);
      }

      if (text) {
        handleNlpSearch(text);
      } else {
        Alert.alert('Analysis Failed', data.message || 'Could not understand audio');
      }

    } catch (err) {
      console.error('Stop recording error:', err);
      setIsRecording(false);
    }
  };



  const handleSaveQuickAdd = async () => {
    try {
      if (quickAddItems.length === 0) return;

      const newMeals = quickAddItems.map(item => ({
        name: item.name,
        mealType: 'snack',
        quantity: parseFloat(item.servingSize),
        unit: item.servingSizeUnit,
        macros: {
          calories: item.calories,
          protein: item.protein,
          carbs: item.carbs,
          fats: item.fats
        },
        fdcId: item.fdcId,
        servingSize: item.servingSize,
        servingSizeUnit: item.servingSizeUnit,
        weightInGrams: item.weightInGrams
      }));

      const updatedMeals = todayLog ? [...todayLog.meals, ...newMeals] : [...newMeals];

      const macroLogData = {
        date: new Date().toISOString(),
        meals: updatedMeals,
        waterIntake: todayLog?.waterIntake || 0,
      };

      await MacroService.logMacros(macroLogData);
      Alert.alert('Success', 'Meals logged successfully!');

      setShowQuickAddSummary(false);
      setQuickAddItems([]);
      loadMacroData();
    } catch (error) {
      console.error('Save quick add error:', error);
      Alert.alert('Error', 'Failed to save meals');
    }
  };

  const calculateQuickAddTotals = () => {
    return quickAddItems.reduce((acc, item) => ({
      calories: acc.calories + (item.calories || 0),
      protein: acc.protein + (item.protein || 0),
      carbs: acc.carbs + (item.carbs || 0),
      fats: acc.fats + (item.fats || 0)
    }), { calories: 0, protein: 0, carbs: 0, fats: 0 });
  };



  const handleFoodSelect = (food) => {
    setSelectedFood(food);
    setShowFoodSearch(false);
    setShowAddMeal(true);
    // Set default unit based on food's serving unit
    if (food.servingSizeUnit) {
      const unit = food.servingSizeUnit.toLowerCase();
      if (unit === 'cup' || unit === 'cups') {
        setMealUnit('cups');
      } else if (unit === 'oz' || unit === 'ounce') {
        setMealUnit('serving');
      } else {
        setMealUnit('serving');
      }
    } else {
      setMealUnit('serving');
    }
    setMealQuantity('1');
    setCalculatedMacros(null);
  };

  const logWater = async (amount) => {
    try {
      const currentWater = todayLog?.waterIntake || 0;
      const newWaterAmount = currentWater + amount;

      const macroLogData = {
        date: new Date().toISOString(),
        meals: todayLog?.meals || [],
        waterIntake: newWaterAmount,
      };

      await MacroService.logMacros(macroLogData);
      loadMacroData(); // Refresh data
    } catch (error) {
      console.error('Water logging error:', error);
      Alert.alert('Error', 'Failed to log water intake');
    }
  };

  const calculateMacros = async () => {
    try {
      let macros;

      if (useManualMacros) {
        // Use manual macro input
        macros = {
          calories: parseFloat(manualMacros.calories) || 0,
          protein: parseFloat(manualMacros.protein) || 0,
          carbs: parseFloat(manualMacros.carbs) || 0,
          fats: parseFloat(manualMacros.fats) || 0,
        };
      } else if (selectedFood) {
        // Use calculated macros if available, otherwise calculate
        if (calculatedMacros) {
          macros = calculatedMacros;
        } else {
          macros = await FoodService.calculateMacros(
            selectedFood,
            parseFloat(mealQuantity),
            mealUnit
          );
        }
      } else {
        Alert.alert('Error', 'Please select a food or enter manual macros');
        return;
      }

      // Validate quantity
      if (!mealQuantity || isNaN(parseFloat(mealQuantity)) || parseFloat(mealQuantity) <= 0) {
        Alert.alert('Error', 'Please enter a valid quantity');
        return;
      }

      const mealData = {
        name: useManualMacros ? 'Manual Entry' : selectedFood.name,
        mealType: 'snack', // Default, could be made selectable
        quantity: parseFloat(mealQuantity),
        unit: mealUnit,
        macros: macros,
        // Store food data for editing
        fdcId: selectedFood?.fdcId,
        servingSize: selectedFood?.servingSize,
        servingSizeUnit: selectedFood?.servingSizeUnit,
        weightInGrams: selectedFood?.weightInGrams,
      };

      // Update or add meal to today's log
      let updatedMeals;
      if (editingMealIndex !== null && todayLog?.meals) {
        // Editing existing meal
        updatedMeals = [...todayLog.meals];
        updatedMeals[editingMealIndex] = mealData;
        setEditingMealIndex(null);
      } else {
        // Adding new meal
        updatedMeals = todayLog ? [...todayLog.meals, mealData] : [mealData];
      }

      const macroLogData = {
        date: new Date().toISOString(),
        meals: updatedMeals,
        waterIntake: todayLog?.waterIntake || 0,
      };

      await MacroService.logMacros(macroLogData);
      Alert.alert('Success', editingMealIndex !== null ? 'Meal updated successfully!' : 'Meal logged successfully!');
      setShowAddMeal(false);
      setSelectedFood(null);
      setMealQuantity('1');
      setMealUnit('serving');
      setCalculatedMacros(null);
      setManualMacros({ calories: '', protein: '', carbs: '', fats: '' });
      setUseManualMacros(false);
      setEditingMealIndex(null);
      loadMacroData();
    } catch (error) {
      console.error('Add meal error:', error);
      Alert.alert('Error', 'Failed to add meal');
    }
  };

  const getProgressPercentage = (current, target) => {
    if (!target || target === 0) return 0;
    return Math.min((current / target) * 100, 100);
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 100) return '#3B82F6';
    if (percentage >= 80) return '#FACC15';
    return '#3B82F6';
  };

  const renderProgressBar = (current, target, label, color) => {
    const percentage = getProgressPercentage(current, target);
    const progressColor = getProgressColor(percentage);

    return (
      <View style={styles.progressContainer}>
        <View style={styles.progressHeader}>
          <Text style={styles.progressLabel}>{label}</Text>
          <Text style={styles.progressValue}>
            {Math.round(current)} / {target}
          </Text>
        </View>
        <View style={styles.progressBar}>
          <View
            style={[
              styles.progressFill,
              {
                width: `${percentage}%`,
                backgroundColor: progressColor
              }
            ]}
          />
        </View>
        <Text style={styles.progressPercentage}>{Math.round(percentage)}%</Text>
      </View>
    );
  };

  const renderFoodItem = ({ item }) => (
    <TouchableOpacity
      style={styles.foodItem}
      onPress={() => handleFoodSelect(item)}
    >
      <View style={styles.foodInfo}>
        <Text style={styles.foodName}>{item.name}</Text>
        {item.brandOwner && (
          <Text style={styles.foodBrand}>{item.brandOwner}</Text>
        )}
        <Text style={styles.foodMacros}>
          {item.calories} cal {item.protein}g protein {item.carbs}g carbs {item.fats}g fats
        </Text>
        <Text style={styles.foodServing}>
          Per {item.servingSize || 100}{item.servingSizeUnit || 'g'}
          {item.weightInGrams && ` (${item.weightInGrams}g)`}
        </Text>
      </View>
      <Ionicons name="chevron-forward" size={20} color="#ccc" />
    </TouchableOpacity>
  );

  const handleDeleteMeal = async (index) => {
    Alert.alert(
      'Delete Meal',
      'Are you sure you want to delete this meal?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              const updatedMeals = todayLog?.meals?.filter((_, i) => i !== index) || [];
              const macroLogData = {
                date: new Date().toISOString(),
                meals: updatedMeals,
                waterIntake: todayLog?.waterIntake || 0,
              };
              await MacroService.logMacros(macroLogData);
              loadMacroData();
            } catch (error) {
              console.error('Delete meal error:', error);
              Alert.alert('Error', 'Failed to delete meal');
            }
          },
        },
      ]
    );
  };

  const handleEditMeal = (meal, index) => {
    // If meal has original food data, try to find it
    // Otherwise, allow manual editing
    setEditingMealIndex(index);

    // Try to reconstruct food object from meal data
    const foodData = {
      fdcId: meal.fdcId || 'manual',
      name: meal.name,
      calories: meal.macros.calories / (meal.quantity || 1),
      protein: meal.macros.protein / (meal.quantity || 1),
      carbs: meal.macros.carbs / (meal.quantity || 1),
      fats: meal.macros.fats / (meal.quantity || 1),
      servingSize: meal.servingSize || 100,
      servingSizeUnit: meal.servingSizeUnit || meal.unit || 'serving',
      weightInGrams: meal.weightInGrams || 100,
    };

    setSelectedFood(foodData);
    setMealQuantity(String(meal.quantity || 1));
    setMealUnit(meal.unit || 'serving');
    setUseManualMacros(false);
    setShowAddMeal(true);
  };

  const renderMealItem = (meal, index) => (
    <View key={index} style={styles.mealItem}>
      <View style={styles.mealItemContent}>
        <View style={styles.mealHeader}>
          <Text style={styles.mealName}>{meal.name}</Text>
          <Text style={styles.mealQuantity}>
            {meal.quantity} {meal.unit}
          </Text>
        </View>
        <View style={styles.mealMacros}>
          <Text style={styles.mealMacroText}>
            {meal.macros.calories} cal {meal.macros.protein}g protein {meal.macros.carbs}g carbs {meal.macros.fats}g fats
          </Text>
        </View>
      </View>
      <View style={styles.mealActions}>
        <TouchableOpacity
          style={styles.mealActionButton}
          onPress={() => handleEditMeal(meal, index)}
        >
          <Ionicons name="pencil" size={20} color="#3B82F6" />
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.mealActionButton}
          onPress={() => handleDeleteMeal(index)}
        >
          <Ionicons name="trash-outline" size={20} color="#FF3B30" />
        </TouchableOpacity>
      </View>
    </View>
  );

  // Scanner state
  const cameraRef = useRef(null);
  const [showScanner, setShowScanner] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [permission, requestPermission] = useCameraPermissions();
  const [detector, setDetector] = useState(null);
  const [scanLoading, setScanLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const supportedFormats = await BarcodeDetector.getSupportedFormats();
        if (supportedFormats.includes("qr_code") || supportedFormats.includes("ean_13")) { // Check for common formats
          setDetector(
            new BarcodeDetector({
              formats: [
                "aztec",
                "code_128",
                "code_39",
                "code_93",
                "codabar",
                "data_matrix",
                "ean_13",
                "ean_8",
                "itf",
                "pdf417",
                "qr_code",
                "upc_a",
                "upc_e",
              ],
            })
          );
        } else {
          // Fallback init if supportedFormats check is strict
          setDetector(new BarcodeDetector({ formats: ["qr_code", "ean_13", "upc_a"] }));
        }
      } catch (e) {
        console.log("Barcode Detector Error", e);
      }
    })();
  }, []);

  const handleScan = async () => {
    if (!permission) return;
    if (!permission.granted) {
      const { granted } = await requestPermission();
      if (!granted) {
        Alert.alert("Permission required", "Camera permission is needed to scan barcodes.");
        return;
      }
    }
    setScanned(false);
    setScanLoading(false);
    setShowScanner(true);
  };

  const scanImage = async () => {
    if (!cameraRef.current || !detector || scanned || scanLoading) return;

    try {
      const photo = await cameraRef.current.takePictureAsync({
        base64: false,
        skipProcessing: true,
      });

      const blob = await fetch(photo.uri).then((r) => r.blob());
      const barcodes = await detector.detect(blob);

      if (barcodes.length > 0) {
        setScanned(true);
        const code = barcodes[0].rawValue;
        console.log("Scanned barcode:", code);
        handleBarcodeLookup(code);
      } else {
        Alert.alert("No barcode found", "Please try aligning the barcode clearly within the frame.");
      }
    } catch (err) {
      console.error("Scan error:", err);
      setScanLoading(false);
      setScanned(false);
      Alert.alert("Error", "Failed to scan image.");
    }
  };

  const handleBarcodeLookup = async (code) => {
    setScanLoading(true);
    try {
      const response = await fetch(`https://training-application-q6ln.onrender.com/api/food/barcode/${code}`);
      const json = await response.json();

      if (json.success && json.item) {
        setShowScanner(false);
        setScanLoading(false);
        setScanned(false);

        // Open the Add Meal screen for the scanned product to allow weight adjustments
        handleFoodSelect(json.item);

        // If there's a message (e.g., from Groq AI fallback), show it after a brief delay
        if (json.message) {
          setTimeout(() => {
            Alert.alert("Barcode Info", json.message);
          }, 300);
        }
      } else {
        setScanLoading(false);
        setScanned(false);
        Alert.alert("Not Found", json.message || "Product not found.");
      }
    } catch (error) {
      console.error("Barcode lookup error:", error);
      setScanLoading(false);
      setScanned(false);
      Alert.alert("Error", "Failed to fetch product data.");
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.safeArea} edges={['top', 'left', 'right']}>
        <View style={styles.loadingContainer}>
          <Text style={{ color: '#FFFFFF' }}>Loading macro data...</Text>
        </View>
      </SafeAreaView>
    );
  }

  const totals = todayLog?.totals || { calories: 0, protein: 0, carbs: 0, fats: 0 };
  const targets = macroTargets || { calories: 2000, protein: 150, carbs: 200, fats: 80 };

  return (
    <View style={[styles.safeArea, { paddingTop: insets.top }]}>
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Header */}
        <View style={[styles.header, { paddingTop: 0 }]}>
          <Text style={styles.title}>Macro Tracker</Text>
          <Text style={styles.subtitle}>Today's Nutrition</Text>
        </View>

        {/* Progress Overview */}
        <View style={styles.progressContainer}>
          <Text style={styles.progressTitle}>Daily Progress</Text>

          {renderProgressBar(totals.calories, targets.calories, 'Calories', '#3B82F6')}
          {renderProgressBar(totals.protein, targets.protein, 'Protein', '#3B82F6')}
          {renderProgressBar(totals.carbs, targets.carbs, 'Carbs', '#FACC15')}
          {renderProgressBar(totals.fats, targets.fats, 'Fats', '#AF52DE')}
        </View>

        {/* Quick Actions */}
        <View style={styles.actionsContainer}>
          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => setShowFoodSearch(true)}
          >
            <Ionicons name="search" size={20} color="white" />
            <Text style={styles.actionButtonText}>Search</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => setShowAddMeal(true)}
          >
            <Ionicons name="add" size={20} color="white" />
            <Text style={styles.actionButtonText}>Add</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.actionButton}
            onPress={handleScan}
          >
            <Ionicons name="barcode-outline" size={20} color="white" />
            <Text style={styles.actionButtonText}>Scan</Text>
          </TouchableOpacity>
        </View>

        {/* NLP Input Helper */}
        <View style={{ paddingHorizontal: 20, marginBottom: 20 }}>
          <Text style={styles.sectionTitle}>Quick Add</Text>
          <View style={styles.nlpContainer}>
            <TextInput
              ref={nlpInputRef}
              style={styles.nlpInput}
              placeholder={isRecording ? "Listening..." : "Type meal details (e.g. 100g rice)..."}
              placeholderTextColor="#999"
              value={isRecording ? "Listening..." : nlpQuery}
              onChangeText={setNlpQuery}
              onSubmitEditing={handleNlpSearch}
              editable={!isRecording}
              multiline={true}
              textAlignVertical="top"
              returnKeyType="send"
              scrollEnabled={true}
            />

            {/* Mic Button */}
            <TouchableOpacity
              style={[
                styles.nlpButton,
                isRecording ? { backgroundColor: '#0B0E14' } : { backgroundColor: '#0B0E14' }
              ]}
              onPress={isRecording ? stopRecording : startRecording}
            >
              <Ionicons
                name={isRecording ? "stop" : "mic"}
                size={24}
                color={isRecording ? "white" : "#3B82F6"}
              />
            </TouchableOpacity>

            {/* Send Button - Always show if text is present to make it obvious */}
            {!isRecording && nlpQuery.length > 0 && (
              <TouchableOpacity
                style={[styles.nlpButton, { backgroundColor: '#3B82F6', marginLeft: 8 }]}
                onPress={handleNlpSearch}
              >
                {searching ? (
                  <Ionicons name="hourglass" size={24} color="white" />
                ) : (
                  <Ionicons name="send" size={20} color="white" />
                )}
              </TouchableOpacity>
            )}
          </View>
          <Text style={styles.nlpHint}>Use voice or text for quick logging!</Text>
        </View>

        {/* Today's Meals */}
        <View style={styles.mealsContainer}>
          <Text style={styles.mealsTitle}>Today's Meals</Text>
          {todayLog?.meals?.length > 0 ? (
            todayLog.meals.map((meal, index) => renderMealItem(meal, index))
          ) : (
            <View style={styles.noMealsContainer}>
              <Ionicons name="restaurant-outline" size={32} color="#ccc" />
              <Text style={styles.noMealsText}>No meals logged today</Text>
              <Text style={styles.noMealsSubtext}>Start by searching for foods</Text>
            </View>
          )}
        </View>

        {/* Water Intake */}
        <View style={styles.waterContainer}>
          <Text style={styles.waterTitle}>Water Intake</Text>
          <View style={styles.waterProgress}>
            <Text style={styles.waterAmount}>{todayLog?.waterIntake || 0}L</Text>
            <Text style={styles.waterTarget}>/ 3L</Text>
          </View>
          <View style={styles.waterButtons}>
            <TouchableOpacity style={styles.waterButton} onPress={() => logWater(0.25)}>
              <Text style={styles.waterButtonText}>+250ml</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.waterButton} onPress={() => logWater(0.5)}>
              <Text style={styles.waterButtonText}>+500ml</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Barcode Scanner Modal */}
        <Modal
          visible={showScanner}
          animationType="slide"
          presentationStyle="fullScreen"
          onRequestClose={() => {
            setShowScanner(false);
            setScanLoading(false);
            setScanned(false);
          }}
        >
          <View style={styles.scannerContainer}>
            <CameraView
              ref={cameraRef}
              style={StyleSheet.absoluteFill}
              facing="back"
            />
            <View style={styles.scannerOverlay}>
              <View style={styles.scannerHeader}>
                <TouchableOpacity
                  onPress={() => {
                    setShowScanner(false);
                    setScanLoading(false);
                    setScanned(false);
                  }}
                  style={styles.closeScannerButton}
                  disabled={scanLoading}
                >
                  <Ionicons name="close" size={28} color="white" />
                </TouchableOpacity>
                <Text style={styles.scannerTitle}>Scan Barcode</Text>
              </View>
              <View style={styles.scanFrame} />
              <Text style={styles.scanText}>Align barcode within frame</Text>

              {scanLoading ? (
                <View style={{
                  marginTop: 40,
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  paddingHorizontal: 40,
                  paddingVertical: 15,
                  borderRadius: 30,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Text style={{ color: 'white', fontSize: 18, fontWeight: 'bold' }}>Loading product details...</Text>
                </View>
              ) : (
                <TouchableOpacity
                  style={{
                    marginTop: 40,
                    backgroundColor: '#3B82F6',
                    paddingHorizontal: 40,
                    paddingVertical: 15,
                    borderRadius: 30,
                  }}
                  onPress={scanImage}
                  disabled={scanned || scanLoading}
                >
                  <Text style={{ color: 'white', fontSize: 18, fontWeight: 'bold' }}>Capture & Scan</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        </Modal>

        {/* Food Search Modal */}
        <Modal
          visible={showFoodSearch}
          animationType="slide"
          presentationStyle="pageSheet"
        >
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <TouchableOpacity onPress={() => setShowFoodSearch(false)}>
                <Text style={styles.cancelButton}>Cancel</Text>
              </TouchableOpacity>
              <Text style={styles.modalTitle}>Search Food</Text>
              <View style={{ width: 60 }} />
            </View>

            <View style={styles.searchContainer}>
              <TextInput
                style={styles.searchInput}
                placeholder="Search for food..."
                value={searchQuery}
                onChangeText={setSearchQuery}
                onSubmitEditing={searchFoods}
              />
              <TouchableOpacity
                style={styles.searchButton}
                onPress={searchFoods}
                disabled={searching}
              >
                <Ionicons name="search" size={20} color="white" />
              </TouchableOpacity>
            </View>

            <FlatList
              data={searchResults}
              renderItem={renderFoodItem}
              keyExtractor={(item) => item.fdcId}
              style={styles.searchResults}
            />
          </View>
        </Modal>

        {/* Add Meal Modal */}
        <Modal
          visible={showAddMeal}
          animationType="slide"
          presentationStyle="pageSheet"
        >
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <TouchableOpacity onPress={() => {
                setShowAddMeal(false);
                setEditingMealIndex(null);
                setSelectedFood(null);
                setMealQuantity('1');
                setMealUnit('serving');
                setCalculatedMacros(null);
              }}>
                <Text style={styles.cancelButton}>Cancel</Text>
              </TouchableOpacity>
              <Text style={styles.modalTitle}>{editingMealIndex !== null ? 'Edit Meal' : 'Add Meal'}</Text>
              <TouchableOpacity onPress={calculateMacros}>
                <Text style={styles.saveButton}>Save</Text>
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.modalContent}>
              {selectedFood && (
                <View style={styles.selectedFoodContainer}>
                  <Text style={styles.selectedFoodName}>{selectedFood.name}</Text>
                  {selectedFood.brandOwner && (
                    <Text style={styles.foodBrand}>{selectedFood.brandOwner}</Text>
                  )}
                  <Text style={styles.selectedFoodMacros}>
                    Per {selectedFood.servingSize || 100}{selectedFood.servingSizeUnit || 'g'}: {selectedFood.calories?.toFixed(0) || 0} cal {selectedFood.protein?.toFixed(1) || 0}g protein {selectedFood.carbs?.toFixed(1) || 0}g carbs {selectedFood.fats?.toFixed(1) || 0}g fats
                  </Text>
                  {selectedFood.weightInGrams && (
                    <Text style={styles.foodWeight}>
                      Weight: {selectedFood.weightInGrams}g per serving
                    </Text>
                  )}
                  {selectedFood.householdServing && (
                    <Text style={styles.householdServing}>
                      {selectedFood.householdServing}
                    </Text>
                  )}
                </View>
              )}

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Quantity</Text>
                <TextInput
                  style={styles.input}
                  value={mealQuantity}
                  onChangeText={setMealQuantity}
                  keyboardType="decimal-pad"
                  placeholder="Enter quantity"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Unit</Text>
                <View style={styles.unitSelector}>
                  {getUnitOptions().map((unit) => (
                    <TouchableOpacity
                      key={unit}
                      style={[
                        styles.unitButton,
                        mealUnit === unit && styles.unitButtonSelected
                      ]}
                      onPress={() => setMealUnit(unit)}
                    >
                      <Text style={[
                        styles.unitButtonText,
                        mealUnit === unit && styles.unitButtonTextSelected
                      ]}>
                        {unit === 'g' ? 'Grams' : unit === 'cups' ? 'Cups' : 'Serving'}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              {calculatedMacros && !useManualMacros && selectedFood && (
                <View style={styles.calculatedMacrosContainer}>
                  <Text style={styles.calculatedMacrosTitle}>
                    Calculated Macros for {mealQuantity} {mealUnit === 'g' ? 'grams' : mealUnit === 'cups' ? 'cup(s)' : 'serving(s)'}:
                  </Text>
                  <View style={styles.calculatedMacrosGrid}>
                    <View style={styles.calculatedMacroItem}>
                      <Text style={styles.calculatedMacroValue}>{Math.round(calculatedMacros.calories)}</Text>
                      <Text style={styles.calculatedMacroLabel}>Calories</Text>
                    </View>
                    <View style={styles.calculatedMacroItem}>
                      <Text style={styles.calculatedMacroValue}>{calculatedMacros.protein.toFixed(1)}</Text>
                      <Text style={styles.calculatedMacroLabel}>Protein (g)</Text>
                    </View>
                    <View style={styles.calculatedMacroItem}>
                      <Text style={styles.calculatedMacroValue}>{calculatedMacros.carbs.toFixed(1)}</Text>
                      <Text style={styles.calculatedMacroLabel}>Carbs (g)</Text>
                    </View>
                    <View style={styles.calculatedMacroItem}>
                      <Text style={styles.calculatedMacroValue}>{calculatedMacros.fats.toFixed(1)}</Text>
                      <Text style={styles.calculatedMacroLabel}>Fats (g)</Text>
                    </View>
                  </View>
                </View>
              )}

              {/* Manual Macro Input */}
              <View style={styles.manualMacrosContainer}>
                <TouchableOpacity
                  style={styles.toggleButton}
                  onPress={() => setUseManualMacros(!useManualMacros)}
                >
                  <Ionicons
                    name={useManualMacros ? "checkmark-circle" : "checkmark-circle-outline"}
                    size={20}
                    color={useManualMacros ? "#3B82F6" : "#ccc"}
                  />
                  <Text style={styles.toggleButtonText}>Enter macros manually</Text>
                </TouchableOpacity>

                {useManualMacros && (
                  <View style={styles.manualMacrosInputs}>
                    <View style={styles.macroRow}>
                      <View style={styles.macroInputGroup}>
                        <Text style={styles.macroLabel}>Calories</Text>
                        <TextInput
                          style={styles.macroInput}
                          value={manualMacros.calories}
                          onChangeText={(value) => setManualMacros({ ...manualMacros, calories: value })}
                          keyboardType="numeric"
                          placeholder="0"
                        />
                      </View>
                      <View style={styles.macroInputGroup}>
                        <Text style={styles.macroLabel}>Protein (g)</Text>
                        <TextInput
                          style={styles.macroInput}
                          value={manualMacros.protein}
                          onChangeText={(value) => setManualMacros({ ...manualMacros, protein: value })}
                          keyboardType="numeric"
                          placeholder="0"
                        />
                      </View>
                    </View>
                    <View style={styles.macroRow}>
                      <View style={styles.macroInputGroup}>
                        <Text style={styles.macroLabel}>Carbs (g)</Text>
                        <TextInput
                          style={styles.macroInput}
                          value={manualMacros.carbs}
                          onChangeText={(value) => setManualMacros({ ...manualMacros, carbs: value })}
                          keyboardType="numeric"
                          placeholder="0"
                        />
                      </View>
                      <View style={styles.macroInputGroup}>
                        <Text style={styles.macroLabel}>Fats (g)</Text>
                        <TextInput
                          style={styles.macroInput}
                          value={manualMacros.fats}
                          onChangeText={(value) => setManualMacros({ ...manualMacros, fats: value })}
                          keyboardType="numeric"
                          placeholder="0"
                        />
                      </View>
                    </View>
                  </View>
                )}
              </View>
            </ScrollView>
          </View>
        </Modal>

        {/* Quick Add Summary Modal */}
        <Modal
          visible={showQuickAddSummary}
          animationType="slide"
          presentationStyle="pageSheet"
        >
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <TouchableOpacity onPress={() => setShowQuickAddSummary(false)}>
                <Text style={styles.cancelButton}>Cancel</Text>
              </TouchableOpacity>
              <Text style={styles.modalTitle}>Quick Add Summary</Text>
              <TouchableOpacity onPress={handleSaveQuickAdd}>
                <Text style={styles.saveButton}>Save All</Text>
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.modalContent}>
              <View style={styles.summaryTotalsContainer}>
                <Text style={styles.summaryTotalsTitle}>Total Nutrition</Text>
                <View style={styles.calculatedMacrosGrid}>
                  <View style={styles.calculatedMacroItem}>
                    <Text style={styles.calculatedMacroValue}>{Math.round(calculateQuickAddTotals().calories)}</Text>
                    <Text style={styles.calculatedMacroLabel}>Calories</Text>
                  </View>
                  <View style={styles.calculatedMacroItem}>
                    <Text style={styles.calculatedMacroValue}>{calculateQuickAddTotals().protein.toFixed(1)}</Text>
                    <Text style={styles.calculatedMacroLabel}>Protein (g)</Text>
                  </View>
                  <View style={styles.calculatedMacroItem}>
                    <Text style={styles.calculatedMacroValue}>{calculateQuickAddTotals().carbs.toFixed(1)}</Text>
                    <Text style={styles.calculatedMacroLabel}>Carbs (g)</Text>
                  </View>
                  <View style={styles.calculatedMacroItem}>
                    <Text style={styles.calculatedMacroValue}>{calculateQuickAddTotals().fats.toFixed(1)}</Text>
                    <Text style={styles.calculatedMacroLabel}>Fats (g)</Text>
                  </View>
                </View>
              </View>

              <Text style={[styles.sectionTitle, { marginTop: 20 }]}>Items to Add</Text>
              {quickAddItems.map((item, index) => (
                <View key={index} style={styles.mealItem}>
                  <View style={styles.mealItemContent}>
                    <View style={styles.mealHeader}>
                      <Text style={styles.mealName}>{item.name}</Text>
                      <Text style={styles.mealQuantity}>{item.servingSize} {item.servingSizeUnit}</Text>
                    </View>
                    <View style={styles.mealMacros}>
                      <Text style={styles.mealMacroText}>
                        {Math.round(item.calories)} cal {item.protein}g P {item.carbs}g C {item.fats}g F
                      </Text>
                    </View>
                  </View>
                </View>
              ))}
            </ScrollView>
          </View>
        </Modal>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#0B0E14',
  },
  container: {
    flex: 1,
    backgroundColor: '#0B0E14',
  },
  scrollContent: {
    paddingBottom: 20,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: SCREEN_HEIGHT * 0.7,
  },
  header: {
    backgroundColor: '#161C2D',
    paddingHorizontal: Math.max(16, SCREEN_WIDTH * 0.05),
    // paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#2C365E',
    shadowcolor: '#FFFFFF',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
    minHeight: 80,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  subtitle: {
    fontSize: Math.max(16, SCREEN_WIDTH * 0.04),
    color: '#6D759E',
    marginTop: 4,
  },
  progressContainer: {
    backgroundColor: '#161C2D',
    margin: 20,
    borderRadius: Math.max(16, SCREEN_WIDTH * 0.04),
    padding: Math.max(16, SCREEN_WIDTH * 0.04),
    shadowcolor: '#FFFFFF',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  progressTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
    marginBottom: Math.max(16, SCREEN_WIDTH * 0.04),
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  progressLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#FFFFFF',
  },
  progressValue: {
    fontSize: 14,
    color: '#6D759E',
  },
  progressBar: {
    height: 8,
    backgroundColor: '#0B0E14',
    borderRadius: 4,
    marginBottom: 4,
  },
  progressFill: {
    height: '100%',
    borderRadius: 4,
  },
  progressPercentage: {
    fontSize: 12,
    color: '#6D759E',
    textAlign: 'right',
  },
  actionsContainer: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    marginBottom: 20,
  },
  actionButton: {
    flex: 1,
    backgroundColor: '#3B82F6',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: Math.max(16, SCREEN_WIDTH * 0.04),
    borderRadius: 12,
    marginHorizontal: 4,
  },
  actionButtonText: {
    color: 'white',
    fontSize: Math.max(16, SCREEN_WIDTH * 0.04),
    fontWeight: '600',
    marginLeft: 8,
  },
  scannerContainer: {
    flex: 1,
    backgroundcolor: '#FFFFFF',
  },
  scannerHeader: {
    position: 'absolute',
    top: 50,
    left: 20,
    right: 20,
    zIndex: 10,
    flexDirection: 'row',
    alignItems: 'center',
  },
  closeScannerButton: {
    padding: 8,
    backgroundColor: 'rgba(0,0,0,0.5)',
    borderRadius: 20,
  },
  scannerTitle: {
    color: 'white',
    fontSize: 18,
    fontWeight: 'bold',
    marginLeft: Math.max(16, SCREEN_WIDTH * 0.04),
  },
  scannerOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 5,
  },
  scanFrame: {
    width: 250,
    height: 250,
    borderWidth: 2,
    borderColor: '#3B82F6',
    borderRadius: 20,
    backgroundColor: 'transparent',
  },
  scanText: {
    color: 'white',
    marginTop: 20,
    fontSize: Math.max(16, SCREEN_WIDTH * 0.04),
    fontWeight: '500',
    backgroundColor: 'rgba(0,0,0,0.6)',
    paddingHorizontal: Math.max(16, SCREEN_WIDTH * 0.04),
    paddingVertical: 8,
    borderRadius: 8,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
    marginBottom: 12,
  },
  nlpContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#161C2D',
    borderRadius: 12,
    padding: 4,
    shadowcolor: '#FFFFFF',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
    borderWidth: 1,
    borderColor: '#e1e5e9',
    minHeight: 52,
  },
  nlpInput: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: Math.max(16, SCREEN_WIDTH * 0.04),
    fontSize: Math.max(16, SCREEN_WIDTH * 0.04),
    color: '#FFFFFF',
    minHeight: 44,
    maxHeight: 120,
    textAlignVertical: 'top',
  },
  nlpButton: {
    backgroundColor: '#3B82F6',
    padding: 10,
    borderRadius: 8,
    marginRight: 4,
    marginTop: 4,
    alignSelf: 'flex-start',
  },
  nlpHint: {
    fontSize: 12,
    color: '#888',
    marginTop: 6,
    marginLeft: 4,
  },
  mealsContainer: {
    backgroundColor: '#161C2D',
    margin: 20,
    marginTop: 0,
    borderRadius: Math.max(16, SCREEN_WIDTH * 0.04),
    padding: Math.max(16, SCREEN_WIDTH * 0.04),
    shadowcolor: '#FFFFFF',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  mealsTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
    marginBottom: Math.max(16, SCREEN_WIDTH * 0.04),
  },
  mealItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  mealItemContent: {
    flex: 1,
  },
  mealHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  mealActions: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 12,
  },
  mealActionButton: {
    padding: 8,
    marginLeft: 8,
  },
  mealName: {
    fontSize: Math.max(16, SCREEN_WIDTH * 0.04),
    fontWeight: '500',
    color: '#FFFFFF',
  },
  mealQuantity: {
    fontSize: 14,
    color: '#6D759E',
  },
  mealMacros: {
    marginTop: 4,
  },
  mealMacroText: {
    fontSize: 14,
    color: '#6D759E',
  },
  noMealsContainer: {
    alignItems: 'center',
    padding: 40,
  },
  noMealsText: {
    fontSize: Math.max(16, SCREEN_WIDTH * 0.04),
    fontWeight: '500',
    color: '#6D759E',
    marginTop: 12,
  },
  noMealsSubtext: {
    fontSize: 14,
    color: '#6D759E',
    marginTop: 4,
  },
  waterContainer: {
    backgroundColor: '#161C2D',
    margin: 20,
    marginTop: 0,
    borderRadius: Math.max(16, SCREEN_WIDTH * 0.04),
    padding: Math.max(16, SCREEN_WIDTH * 0.04),
    shadowcolor: '#FFFFFF',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  waterTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
    marginBottom: 12,
  },
  waterProgress: {
    flexDirection: 'row',
    alignItems: 'baseline',
    marginBottom: Math.max(16, SCREEN_WIDTH * 0.04),
  },
  waterAmount: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#3B82F6',
  },
  waterTarget: {
    fontSize: Math.max(16, SCREEN_WIDTH * 0.04),
    color: '#6D759E',
    marginLeft: 4,
  },
  waterButtons: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  waterButton: {
    backgroundColor: '#0B0E14',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 20,
  },
  waterButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#FFFFFF',
  },
  modalContainer: {
    flex: 1,
    backgroundColor: '#0B0E14',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    paddingTop: 60,
    backgroundColor: '#161C2D',
    borderBottomWidth: 1,
    borderBottomColor: '#e1e5e9',
  },
  cancelButton: {
    fontSize: Math.max(16, SCREEN_WIDTH * 0.04),
    color: '#3B82F6',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  saveButton: {
    fontSize: Math.max(16, SCREEN_WIDTH * 0.04),
    color: '#3B82F6',
    fontWeight: '600',
  },
  searchContainer: {
    flexDirection: 'row',
    padding: 20,
    backgroundColor: '#161C2D',
  },
  searchInput: {
    flex: 1,
    backgroundColor: '#0B0E14',
    color: '#FFFFFF',
    padding: Math.max(16, SCREEN_WIDTH * 0.04),
    borderRadius: 12,
    fontSize: Math.max(16, SCREEN_WIDTH * 0.04),
    marginRight: 12,
  },
  searchButton: {
    backgroundColor: '#3B82F6',
    padding: Math.max(16, SCREEN_WIDTH * 0.04),
    borderRadius: 12,
  },
  searchResults: {
    flex: 1,
    paddingHorizontal: 20,
  },
  foodItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: Math.max(16, SCREEN_WIDTH * 0.04),
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  foodInfo: {
    flex: 1,
  },
  foodName: {
    fontSize: Math.max(16, SCREEN_WIDTH * 0.04),
    fontWeight: '500',
    color: '#FFFFFF',
    marginBottom: 4,
  },
  foodBrand: {
    fontSize: 14,
    color: '#6D759E',
    marginBottom: 4,
  },
  foodMacros: {
    fontSize: 12,
    color: '#6D759E',
  },
  modalContent: {
    flex: 1,
    padding: 20,
  },
  selectedFoodContainer: {
    backgroundColor: '#161C2D',
    padding: Math.max(16, SCREEN_WIDTH * 0.04),
    borderRadius: 12,
    marginBottom: 20,
  },
  selectedFoodName: {
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
    marginBottom: 8,
  },
  selectedFoodMacros: {
    fontSize: 14,
    color: '#6D759E',
  },
  inputGroup: {
    marginBottom: 20,
  },
  inputLabel: {
    fontSize: Math.max(16, SCREEN_WIDTH * 0.04),
    fontWeight: '500',
    color: '#FFFFFF',
    marginBottom: 8,
  },
  input: {
    backgroundColor: '#161C2D',
    color: '#FFFFFF',
    padding: Math.max(16, SCREEN_WIDTH * 0.04),
    borderRadius: 12,
    fontSize: Math.max(16, SCREEN_WIDTH * 0.04),
    borderWidth: 1,
    borderColor: '#e1e5e9',
  },
  // Manual Macro Styles
  manualMacrosContainer: {
    marginTop: 20,
    padding: Math.max(16, SCREEN_WIDTH * 0.04),
    backgroundColor: '#0B0E14',
    borderRadius: 12,
  },
  toggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: Math.max(16, SCREEN_WIDTH * 0.04),
  },
  toggleButtonText: {
    fontSize: Math.max(16, SCREEN_WIDTH * 0.04),
    fontWeight: '500',
    color: '#FFFFFF',
    marginLeft: 8,
  },
  manualMacrosInputs: {
    marginTop: 8,
  },
  macroRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  macroInputGroup: {
    flex: 1,
    marginHorizontal: 4,
  },
  macroLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#FFFFFF',
    marginBottom: 8,
  },
  macroInput: {
    backgroundColor: '#161C2D',
    padding: 12,
    borderRadius: 8,
    fontSize: Math.max(16, SCREEN_WIDTH * 0.04),
    textAlign: 'center',
    borderWidth: 1,
    borderColor: '#e1e5e9',
  },
  foodServing: {
    fontSize: 11,
    color: '#6D759E',
    marginTop: 4,
  },
  foodWeight: {
    fontSize: 12,
    color: '#6D759E',
    marginTop: 4,
  },
  householdServing: {
    fontSize: 12,
    color: '#3B82F6',
    marginTop: 4,
    fontStyle: 'italic',
  },
  unitSelector: {
    flexDirection: 'row',
    marginTop: 8,
    justifyContent: 'space-between',
  },
  unitButton: {
    flex: 1,
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#0B0E14',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e1e5e9',
    marginHorizontal: 4,
  },
  unitButtonSelected: {
    backgroundColor: '#3B82F6',
    borderColor: '#3B82F6',
  },
  unitButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#FFFFFF',
    textTransform: 'capitalize',
  },
  unitButtonTextSelected: {
    color: 'white',
  },
  calculatedMacrosContainer: {
    backgroundColor: '#0B0E14',
    padding: Math.max(16, SCREEN_WIDTH * 0.04),
    borderRadius: 12,
    marginTop: 20,
    borderWidth: 1,
    borderColor: '#3B82F6',
  },
  calculatedMacrosTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#3B82F6',
    marginBottom: 12,
  },
  calculatedMacrosGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  calculatedMacroItem: {
    width: '48%',
    backgroundColor: '#161C2D',
    padding: 12,
    borderRadius: 8,
    marginBottom: 8,
    alignItems: 'center',
  },
  calculatedMacroValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: 4,
  },
  calculatedMacroLabel: {
    fontSize: 12,
    color: '#6D759E',
  },
  summaryTotalsContainer: {
    backgroundColor: '#161C2D',
    padding: Math.max(16, SCREEN_WIDTH * 0.04),
    borderRadius: 12,
    marginBottom: 20,
    shadowcolor: '#FFFFFF',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  summaryTotalsTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 12,
    color: '#FFFFFF'
  }
});

export default MacroTrackerScreen;
